import React, { useEffect } from 'react';
import styled from 'styled-components';
import SanityImage from 'gatsby-plugin-sanity-image';

import breakpoints from '@repo/ui-components/breakpoints';
import type { SanitySupportArticleMediaGrid } from '@repo/models/gatsby-graphql-types';

import SupportArticleText from './SupportArticleText';

const MediaGridContainer = styled.div``;

const MediaGridHeading = styled.h3`
  margin-bottom: 0.75rem;
`;

const MediaGridWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-evenly;
`;

const MediaGridItem = styled.div`
  flex: 0 0 100%;
  margin-bottom: 1.25em;

  a {
    &:hover {
      color: ${({ theme }) => theme.colors.linkHover};
    }
  }

  h2 {
    margin: 0.25em 0;
  }

  img {
    width: 100%;
    margin-bottom: 0.25em;
  }

  @media (min-width: ${breakpoints.md}) {
    flex: 0 0 47%;
  }

  @media (min-width: ${breakpoints.lg}) {
    flex: 0 0 30%;
  }
`;

const MediaGridDescription = styled.div``;

const MediaGridItemMedia = styled.div`
  padding: revert;

  img {
    height: 300px;
    object-fit: contain;
  }

  lite-youtube {
    border: none;
    padding-top: 0;
    height: 300px;
  }
`;

const MediaGridItemText = styled.div`
  display: flex;
  justify-content: center;
`;

interface CustomSanitySupportArticleMediaGrid
  extends SanitySupportArticleMediaGrid {
  heading?: string;
}

interface SupportArticleMediaGridProps {
  section?: CustomSanitySupportArticleMediaGrid;
}

function SupportArticleMediaGrid({
  section,
}: Readonly<SupportArticleMediaGridProps>) {
  useEffect(() => {
    if (globalThis.window !== undefined) {
      import('lite-youtube-embed');
    }
  }, []);

  return (
    <MediaGridContainer>
      {section?.heading && (
        <MediaGridHeading>{section?.heading}</MediaGridHeading>
      )}
      {section?.description && (
        <MediaGridDescription>
          <SupportArticleText text={section?.description.text} />
        </MediaGridDescription>
      )}
      <MediaGridWrapper>
        {section?.items?.map(item => {
          return (
            <MediaGridItem key={item._key}>
              <MediaGridItemMedia>
                {item?.image?.asset && (
                  <SanityImage
                    asset={item?.image?.asset}
                    alt={item?.image?.alt}
                  />
                )}
                {item?.video?.url && !item?.image?.asset && (
                  <lite-youtube
                    title={item?.video?.caption}
                    videoid={item?.video?.id}
                    frameBorder="0"
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{
                      backgroundImage: `url('https://i.ytimg.com/vi/${item?.video?.id}/maxresdefault.jpg')`,
                    }}
                    params="rel=0&listType=playlist&loop=1&showinfo=0&iv_load_policy=30&start=0&end=0"
                  />
                )}
              </MediaGridItemMedia>
              <MediaGridItemText>
                {item?.text && <SupportArticleText text={item?.text?.text} />}
              </MediaGridItemText>
            </MediaGridItem>
          );
        })}
      </MediaGridWrapper>
    </MediaGridContainer>
  );
}

export default SupportArticleMediaGrid;
